import type { BoardData } from '../model/schema.js';
import { mainStage } from '../model/areas.js';
import { normalizeName } from './merge.js';
import type { AiExtraction, AiMaterialExtraction, AiMaterialLine } from './schema.js';

/**
 * Combining extractions (decision U10): a re-run of single pages is merged into the stored
 * extraction, and a fresh recognition into the user's corrected copy, without losing edits.
 */

/** `base` with its missing (undefined) fields taken from `incoming`. */
function fill<T extends object>(base: T, incoming: T): T {
  const out = { ...incoming, ...base };
  for (const [k, v] of Object.entries(base)) {
    if (v === undefined)
      (out as Record<string, unknown>)[k] = (incoming as Record<string, unknown>)[k];
  }
  return out;
}

/** Keys with an occurrence counter, so the 2nd "Wedge" matches the 2nd "Wedge". */
function keyed<T>(list: readonly T[], key: (v: T) => string): string[] {
  const seen = new Map<string, number>();
  return list.map((v) => {
    const k = key(v);
    // Stryker disable next-line ArithmeticOperator: equivalent – both lists use the same counter.
    const n = (seen.get(k) ?? 0) + 1;
    seen.set(k, n);
    return `${k}#${n}`;
  });
}

function mergeList<T extends object>(
  base: readonly T[],
  incoming: readonly T[],
  key: (v: T) => string,
): T[] {
  const out = [...base];
  const baseKeys = keyed(base, key);
  keyed(incoming, key).forEach((k, i) => {
    const at = baseKeys.indexOf(k);
    if (at === -1) out.push(incoming[i]!);
    else out[at] = fill(out[at]!, incoming[i]!);
  });
  return out;
}

function union(a: readonly string[], b: readonly string[]): string[] {
  return [...new Set([...a, ...b])];
}

const n = normalizeName;

/**
 * Merges `incoming` into `base`: entries are identified by channel number, musician/instrument
 * name, mic channel or purpose, etc.; base entries win field by field (gaps are filled),
 * unknown incoming entries are appended, notes and warnings are united.
 */
export function mergeExtractions(base: AiExtraction, incoming: AiExtraction): AiExtraction {
  const stage =
    base.stage === undefined || incoming.stage === undefined
      ? (base.stage ?? incoming.stage)
      : fill(base.stage, incoming.stage);
  return {
    ...(stage !== undefined ? { stage } : {}),
    musicians: mergeList(base.musicians, incoming.musicians, (m) => n(m.name)),
    instruments: mergeList(base.instruments, incoming.instruments, (i) => n(i.name)),
    microphones: mergeList(base.microphones, incoming.microphones, (m) =>
      m.channel === undefined ? n(m.purpose) : `ch${m.channel}`,
    ),
    dis: mergeList(base.dis, incoming.dis, (d) =>
      d.channel === undefined ? n(d.purpose) : `ch${d.channel}`,
    ),
    monitors: mergeList(
      base.monitors,
      incoming.monitors,
      (m) => `${m.type}:${n(m.mix ?? '')}:${n(m.musician ?? '')}`,
    ),
    mixes: mergeList(base.mixes, incoming.mixes, (m) => n(m.name)),
    channels: mergeList(base.channels, incoming.channels, (c) => String(c.number)),
    stageboxes: mergeList(base.stageboxes, incoming.stageboxes, (s) => n(s.name)),
    power: mergeList(base.power, incoming.power, (p) => `${p.voltage}:${n(p.plug ?? '')}`),
    risers: mergeList(base.risers, incoming.risers, (r) => `${r.w}x${r.d}`),
    notes: union(base.notes, incoming.notes),
    warnings: union(base.warnings, incoming.warnings),
  };
}

/** Same for material lists (U11): lines by category + article name. */
export function mergeMaterialExtractions(
  base: AiMaterialExtraction,
  incoming: AiMaterialExtraction,
): AiMaterialExtraction {
  const key = (l: AiMaterialLine) => `${n(l.category ?? '')}:${n(l.name)}`;
  return {
    lines: mergeList(base.lines, incoming.lines, key),
    warnings: union(base.warnings, incoming.warnings),
  };
}

const MAX_CONTEXT_ROWS = 96;

function rows(label: string, values: string[]): string[] {
  if (values.length === 0) return [];
  const shown = values.slice(0, MAX_CONTEXT_ROWS);
  const more = values.length - shown.length;
  return [`${label}: ${shown.join('; ')}${more > 0 ? `; … (+${more})` : ''}`];
}

/**
 * Compact, content-only summary of the board for the LLM prompt (existing channel numbers and
 * names, musicians, mixes, stage size), so the model can refer to them. Empty board → ''.
 */
export function boardContextForAi(board: BoardData): string {
  const items = Object.values(board.items);
  const lines = [
    ...rows(
      'Channels',
      Object.values(board.channels)
        .sort((a, b) => a.number - b.number)
        .map((c) => `${c.number} ${c.name}`.trim()),
    ),
    ...rows(
      'Musicians',
      items.filter((i) => i.kind === 'musician').map((i) => i.label),
    ),
    ...rows(
      'Mixes',
      Object.values(board.mixes)
        .sort((a, b) => a.number - b.number)
        .map((m) => `${m.number} ${m.name} (${m.kind})`),
    ),
  ];
  if (lines.length === 0) return '';
  const frame = mainStage(board);
  const stage = frame === undefined ? [] : [`Stage: ${frame.width} × ${frame.depth} m`];
  return [...stage, ...lines].join('\n');
}
